import React, { useState } from 'react';
import api from '../lib/api';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import './LeadCaptureForm.css';

interface FormData {
  name: string;
  email: string;
  company: string;
}

const LeadCaptureForm: React.FC = () => {
  const showSuccess = useSuccessAlert();
  const showError = useErrorAlert();
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: ''
  });

  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Submit lead to backend API
      const response = await api.leads.submitLead({
        name: formData.name,
        email: formData.email,
        company: formData.company
      });
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to submit lead');
      }
      
      showSuccess('Thank you for your interest! We will contact you soon.');
      setFormData({ name: '', email: '', company: '' }); // Reset form
    } catch (error) {
      console.error('Form submission error:', error);
      showError(error instanceof Error ? error.message : 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="lead-capture-section">
      <div className="lead-capture-background" />
      
      <div className="container">
        <div className="lead-capture-container">
          
          {/* Section Header */}
          <div className="lead-capture-header">
            <div className="lead-capture-badge">
              <div className="lead-capture-badge-dot" />
              Get Started
            </div>
            
            <h2 className="lead-capture-title">Ready to Secure Your Business?</h2>
            
            <p className="lead-capture-subtitle">
              Get a personalized cybersecurity assessment and see how we can protect your organization from threats.
            </p>
          </div>
          
          {/* Lead Capture Form */}
          <div className="lead-capture-form-wrapper">
            <form className="lead-capture-form" onSubmit={handleSubmit}>
              
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-input ${errors.name ? 'form-input-error' : ''}`}
                  placeholder="Enter your full name"
                />
                {errors.name && <span className="form-error">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                  placeholder="Enter your email address"
                />
                {errors.email && <span className="form-error">{errors.email}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="company" className="form-label">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your company name (optional)"
                />
              </div>
              
              <button 
                type="submit" 
                className="lead-btn lead-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="btn-spinner" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <span>Get Free Assessment</span>
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
              
              <p className="form-terms">
                By submitting this form, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>. 
                We'll only use your information to contact you about cybersecurity services.
              </p>
              
            </form>
            
            {/* Feature Highlights */}
            <div className="lead-features">
              <div className="lead-feature">
                <i className="ri-shield-check-line"></i>
                <span>Free Security Assessment</span>
              </div>
              <div className="lead-feature">
                <i className="ri-time-line"></i>
                <span>Response within 24 hours</span>
              </div>
              <div className="lead-feature">
                <i className="ri-customer-service-2-line"></i>
                <span>Direct access to security experts</span>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureForm;
